<template>
    <div class="shipment-form">
        <div class="shipment-form__content">
            <!-- Sender Selection Fields -->
            <div class="shipment-form__senders-section">
                <div
                    v-for="(senderField, index) in senderFields"
                    :key="senderField.id"
                    class="shipment-form__sender-field-wrapper"
                >
                    <v-autocomplete
                        :key="`sender-${senderField.id}-${senderFieldRevertCounter}`"
                        :value="senderField.value"
                        :items="usersItems"
                        item-text="text"
                        item-value="value"
                        :label="$t('pages.shipments.selectUser')"
                        outlined
                        dense
                        hide-no-data
                        hide-selected
                        clearable
                        hide-details
                        :loading="isLoadingUsers"
                        :disabled="isLoadingUsers"
                        :error-messages="getSenderError(index)"
                        class="shipment-form__sender-field"
                        @change="onSenderFieldChange(index, $event)"
                        @blur="markSenderFieldTouched(index)"
                    ></v-autocomplete>
                    <v-btn
                        v-if="senderFields.length > 1"
                        icon
                        small
                        color="error"
                        class="shipment-form__remove-sender-btn"
                        @click="removeSenderField(index)"
                    >
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </div>
                <v-btn
                    text
                    small
                    color="primary"
                    class="shipment-form__add-sender-btn"
                    :disabled="!canAddSender"
                    @click="addSenderField"
                >
                    <v-icon small left>mdi-plus</v-icon>
                    {{ $t('pages.shipments.addSender') || 'Add User' }}
                </v-btn>
            </div>

            <!-- Divider between Senders and Recipient -->
            <v-divider
                v-if="hasSelectedSenders"
                class="shipment-form__section-divider"
            ></v-divider>

            <!-- Recipient: tabs (select existing OR enter manually) -->
            <div v-if="hasSelectedSenders" class="shipment-form__recipient">
                <v-tabs
                    :value="recipientMode"
                    grow
                    class="shipment-form__recipient-tabs"
                    @change="onRecipientModeChange"
                >
                    <v-tab href="#select">
                        {{ $t('pages.shipments.selectRecipient') }}
                    </v-tab>
                    <v-tab href="#manual">
                        {{ $t('pages.shipments.recipientName') }}
                    </v-tab>
                </v-tabs>
                <v-tabs-items :value="recipientMode" class="shipment-form__recipient-tabs-items">
                    <v-tab-item value="select">
                        <v-autocomplete
                            :value="selectedRecipientId"
                            :items="recipientsItems"
                            item-text="text"
                            item-value="value"
                            :label="$t('pages.shipments.selectRecipient')"
                            outlined
                            dense
                            hide-no-data
                            hide-selected
                            clearable
                            :loading="isLoadingRecipients"
                            :disabled="isLoadingRecipients || !hasSelectedSenders"
                            class="shipment-form__recipient-field"
                            @input="onRecipientSelect"
                        ></v-autocomplete>
                    </v-tab-item>
                    <v-tab-item value="manual">
                        <v-text-field
                            :value="recipientFullName"
                            :label="$t('pages.shipments.recipientName')"
                            outlined
                            dense
                            class="shipment-form__recipient-field"
                            @input="onRecipientNameChange"
                        ></v-text-field>
                    </v-tab-item>
                </v-tabs-items>
            </div>

            <!-- Selected Items with Prices -->
            <div v-if="selectedItems.length > 0" class="shipment-form__section">
                <div class="shipment-form__section-header">
                    <h3 class="shipment-form__section-title">
                        {{ $t('pages.shipments.items') }}
                        <span class="shipment-form__items-count"
                            >({{ selectedItems.length }})</span
                        >
                    </h3>
                </div>

                <div class="shipment-form__items-list">
                    <div
                        v-for="(group, groupIndex) in groupedItemsBySender"
                        :key="group.sender"
                        class="shipment-form__sender-wrapper"
                    >
                        <div class="shipment-form__sender-group">
                            <h4 class="shipment-form__sender-name">
                                {{ group.sender }}
                            </h4>
                            <div class="shipment-form__sender-items">
                                <div
                                    v-for="item in group.items"
                                    :key="item.wig.id"
                                    class="shipment-form__item-card"
                                >
                                    <div class="shipment-form__item-header">
                                        <div class="shipment-form__item-info">
                                            <div
                                                class="shipment-form__item-info-row"
                                            >
                                                <strong
                                                    >{{
                                                        $t(
                                                            'pages.shipments.client'
                                                        )
                                                    }}:</strong
                                                >
                                                <span>{{
                                                    item.wig.client
                                                }}</span>
                                            </div>
                                            <div
                                                class="shipment-form__item-info-row"
                                            >
                                                <strong
                                                    >{{
                                                        $t(
                                                            'pages.shipments.barcode'
                                                        )
                                                    }}:</strong
                                                >
                                                <span
                                                    class="shipment-form__barcode-value"
                                                >
                                                    {{ item.wig.barcode }}
                                                    <v-icon
                                                        v-if="item.wig.is_repair"
                                                        small
                                                        color="error"
                                                    >
                                                        mdi-wrench
                                                    </v-icon>
                                                </span>
                                            </div>
                                        </div>
                                        <v-btn
                                            icon
                                            color="error"
                                            small
                                            @click.stop="
                                                $emit(
                                                    'remove-item',
                                                    item.wig.id
                                                )
                                            "
                                        >
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                    <div class="shipment-form__price-wrapper">
                                        <v-text-field
                                            :value="item.price"
                                            :label="$t('pages.shipments.price')"
                                            type="number"
                                            outlined
                                            dense
                                            :rules="priceRulesFor(item)"
                                            :required="!isItemRepair(item)"
                                            step="0.01"
                                            min="0"
                                            :error-messages="
                                                getPriceError(item)
                                            "
                                            class="shipment-form__item-field shipment-form__price-input"
                                            @input="
                                                updateItemPrice(
                                                    item.wig.id,
                                                    $event
                                                )
                                            "
                                        ></v-text-field>
                                        <div
                                            class="shipment-form__currency-display"
                                        >
                                            {{
                                                getCurrencySymbol(
                                                    item.wig.price_currency
                                                )
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <v-divider
                            v-if="groupIndex < groupedItemsBySender.length - 1"
                            class="shipment-form__sender-divider"
                        ></v-divider>
                    </div>
                </div>
            </div>

            <!-- Empty State for Items -->
            <div
                v-else-if="hasSelectedSenders"
                class="shipment-form__empty-state"
            >
                <v-icon large color="grey">mdi-cart-outline</v-icon>
                <p>
                    {{
                        $t('pages.shipments.selectWigsToAdd') ||
                        'Select wigs from the left to add items'
                    }}
                </p>
            </div>
        </div>

        <div class="shipment-form__actions">
            <v-btn text outlined @click="$emit('cancel')">
                {{ $t('pages.shipments.cancel') }}
            </v-btn>
            <v-tooltip v-if="!canSubmit" bottom>
                <template #activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                        <v-btn
                            color="primary"
                            :disabled="!canSubmit"
                            @click="$emit('submit')"
                        >
                            {{ $t('pages.shipments.create') }}
                        </v-btn>
                    </span>
                </template>
                <span>{{ submitTooltip }}</span>
            </v-tooltip>
            <v-btn
                v-else
                dark
                color="#967adc"
                :loading="isSubmitting"
                @click="$emit('submit')"
            >
                {{ $t('pages.shipments.create') }}
            </v-btn>
        </div>
    </div>
</template>

<script>
import languageMixin from '@/mixins/language.mixin'
import { mapActions } from 'vuex'
import { actionTypes as confirmerActionTypes } from '@/store/modules/confirmer'
import { getCurrencySymbol } from '@/helpers/format-price-with-currency'

export default {
    name: 'ShipmentForm',
    mixins: [languageMixin],
    props: {
        selectedSenders: {
            type: Array,
            default: () => [],
        },
        usersItems: {
            type: Array,
            default: () => [],
        },
        isLoadingUsers: {
            type: Boolean,
            default: false,
        },
        selectedItems: {
            type: Array,
            default: () => [],
        },
        isSubmitting: {
            type: Boolean,
            default: false,
        },
        recipientsItems: {
            type: Array,
            default: () => [],
        },
        isLoadingRecipients: {
            type: Boolean,
            default: false,
        },
        selectedRecipientId: {
            type: [Number, String],
            default: null,
        },
        recipientFirstName: {
            type: String,
            default: '',
        },
        recipientLastName: {
            type: String,
            default: '',
        },
    },
    emits: [
        'update:selectedSenders',
        'senders-change',
        'remove-item',
        'cancel',
        'submit',
        'update-item-price',
        'update:selectedRecipientId',
        'update:recipientFirstName',
        'update:recipientLastName',
    ],
    data: function () {
        return {
            nextSenderFieldId: 1,
            touchedSenderFields: {},
            recipientMode: 'select',
            senderFieldRevertCounter: 0
        }
    },
    computed: {
        rules: function () {
            return {
                required: (value) => {
                    if (Array.isArray(value)) {
                        return (
                            value.length > 0 ||
                            this.$t('pages.shipments.required')
                        )
                    }
                    return !!value || this.$t('pages.shipments.required')
                },
                positiveNumber: (value) =>
                    value > 0 || this.$t('pages.shipments.priceMustBePositive'),
                nonNegativeNumber: (value) =>
                    this.isPriceEmpty(value) ||
                    Number(value) >= 0 ||
                    this.$t('pages.shipments.priceMustBePositive'),
                recipientNameRequired: (value) => {
                    const trimmedValue = (value || '').trim()
                    return (
                        trimmedValue.length > 0 ||
                        this.$t('pages.shipments.recipientNameRequired')
                    )
                },
            }
        },
        recipientFullName: function () {
            return (this.recipientFirstName || '').trim()
        },
        hasSelectedSenders: function () {
            return (
                Array.isArray(this.selectedSenders) &&
                this.selectedSenders.length > 0
            )
        },
        senderFields: function () {
            // Convert selectedSenders array to field structure
            if (
                !Array.isArray(this.selectedSenders) ||
                this.selectedSenders.length === 0
            ) {
                return [{ id: 0, value: null }]
            }
            return this.selectedSenders.map((senderId, index) => ({
                id: index,
                value: senderId,
            }))
        },
        canAddSender: function () {
            // Can add if all existing fields are filled
            return this.senderFields.every(
                (field) => field.value !== null && field.value !== ''
            )
        },
        allSendersFilled: function () {
            return this.selectedSenders.every(
                (sender) =>
                    sender !== null && sender !== '' && sender !== undefined
            )
        },
        hasRecipientName: function () {
            return Boolean(
                this.recipientFullName && this.recipientFullName.trim()
            )
        },
        allItemsHaveBarcode: function () {
            return this.selectedItems.every(
                (item) => item.wig?.barcode && item.wig.barcode.trim() !== ''
            )
        },
        allPricesValid: function () {
            return this.selectedItems.every((item) =>
                this.isItemRepair(item)
                    ? !this.isPriceNegative(item.price)
                    : item.price && item.price > 0
            )
        },
        invalidPriceMessage: function () {
            const hasInvalidNewWig = this.selectedItems.some(
                (item) =>
                    !this.isItemRepair(item) &&
                    (!item.price || item.price <= 0)
            )
            return hasInvalidNewWig
                ? this.$t('pages.shipments.allWigsMustHavePrice')
                : this.$t('pages.shipments.priceMustBePositive')
        },
        isRecipientValid: function () {
            if (this.recipientMode === 'select') {
                return !!this.selectedRecipientId
            }
            if (this.recipientMode === 'manual') {
                return !!(this.recipientFullName && this.recipientFullName.trim())
            }
            return true
        },
        canSubmit: function () {
            return (
                this.hasSelectedSenders &&
                this.allSendersFilled &&
                this.isRecipientValid &&
                this.selectedItems.length > 0 &&
                this.allItemsHaveBarcode &&
                this.allPricesValid &&
                !this.isSubmitting
            )
        },
        managerError: function () {
            return null
        },
        submitValidators: function () {
            return [
                {
                    fails: () => !this.hasSelectedSenders,
                    message: () => this.$t('pages.shipments.userRequired'),
                },
                {
                    fails: () => !this.allSendersFilled,
                    message: () =>
                        this.$t('pages.shipments.allSendersRequired') ||
                        'All sender fields must be filled',
                },
                {
                    fails: () => !this.hasRecipientName,
                    message: () =>
                        this.$t('pages.shipments.recipientNameRequired'),
                },
                {
                    fails: () => this.selectedItems.length === 0,
                    message: () =>
                        this.$t('pages.shipments.atLeastOneWigRequired'),
                },
                {
                    fails: () => !this.allItemsHaveBarcode,
                    message: () =>
                        this.$t('pages.shipments.allWigsMustHaveBarcode'),
                },
                {
                    fails: () => !this.allPricesValid,
                    message: () => this.invalidPriceMessage,
                },
            ]
        },
        submitTooltip: function () {
            if (!this.hasSelectedSenders) {
                return this.$t('pages.shipments.userRequired')
            }

            const allSendersFilled = this.selectedSenders.every(
                (sender) =>
                    sender !== null && sender !== '' && sender !== undefined
            )
            if (!allSendersFilled) {
                return (
                    this.$t('pages.shipments.allSendersRequired') ||
                    'All sender fields must be filled'
                )
            }

            if (
                this.recipientMode === 'select' &&
                !this.selectedRecipientId
            ) {
                return this.$t('pages.shipments.recipientRequired')
            }

            if (
                this.recipientMode === 'manual' &&
                (!this.recipientFullName || !this.recipientFullName.trim())
            ) {
                return this.$t('pages.shipments.recipientNameRequired')
            }

            if (this.selectedItems.length === 0) {
                return this.$t('pages.shipments.atLeastOneWigRequired')
            }

            const itemsWithoutBarcode = this.selectedItems.filter(
                (item) => !item.wig?.barcode || item.wig.barcode.trim() === ''
            )

            if (itemsWithoutBarcode.length > 0) {
                return this.$t('pages.shipments.allWigsMustHaveBarcode')
            }

            const hasInvalidNewWig = this.selectedItems.some(
                (item) =>
                    !this.isItemRepair(item) &&
                    (!item.price || item.price <= 0)
            )
            if (hasInvalidNewWig) {
                return this.$t('pages.shipments.allWigsMustHavePrice')
            }

            const hasNegativeRepair = this.selectedItems.some(
                (item) =>
                    this.isItemRepair(item) && this.isPriceNegative(item.price)
            )
            if (hasNegativeRepair) {
                return this.$t('pages.shipments.priceMustBePositive')
            }

            return ''
        },
        groupedItemsBySender: function () {
            const groups = {}

            this.selectedItems.forEach((item) => {
                const sender = item.wig.managerNickName || '-'
                if (!groups[sender]) {
                    groups[sender] = []
                }
                groups[sender].push(item)
            })

            return Object.keys(groups).map((sender) => ({
                sender,
                items: groups[sender],
            }))
        },
    },
    methods: {
        ...mapActions('confirmer', {
            confirmHandle: confirmerActionTypes.confirmHandle,
        }),
        isItemRepair: function (item) {
            return (
                item?.wig?.is_repair === true || item?.wig?.is_repair === 1
            )
        },
        isPriceEmpty: function (price) {
            return price === null || price === undefined || price === ''
        },
        isPriceNegative: function (price) {
            return !this.isPriceEmpty(price) && Number(price) < 0
        },
        isPriceNonPositive: function (price) {
            return !this.isPriceEmpty(price) && Number(price) <= 0
        },
        updateItemPrice: function (wigId, value) {
            this.$emit('update-item-price', wigId, value)
        },
        priceRulesFor: function (item) {
            return this.isItemRepair(item)
                ? [this.rules.nonNegativeNumber]
                : [this.rules.required, this.rules.positiveNumber]
        },
        getPriceError: function (item) {
            const isInvalid = this.isItemRepair(item)
                ? this.isPriceNegative(item.price)
                : this.isPriceNonPositive(item.price)
            return isInvalid
                ? this.$t('pages.shipments.priceMustBePositive')
                : []
        },
        senderHasSelectedItems: function (senderId) {
            if (senderId == null) return false
            return this.selectedItems.some(
                (item) => item.wig?.managerId === senderId
            )
        },
        confirmRemovingSenderIfNeeded: async function (senderId) {
            if (!this.senderHasSelectedItems(senderId)) {
                return true
            }
            try {
                await this.confirmHandle(
                    this.$t('pages.shipments.confirmRemoveSenderWithItems')
                )
                return true
            } catch (e) {
                return false
            }
        },
        onSenderFieldChange: async function (index, value) {
            const previousValue = this.selectedSenders[index]
            const normalizedValue =
                value === null || value === '' ? null : value

            if (previousValue === normalizedValue) {
                return
            }

            if (this.senderHasSelectedItems(previousValue)) {
                this.senderFieldRevertCounter++
                try {
                    await this.confirmHandle(
                        this.$t('pages.shipments.confirmRemoveSenderWithItems')
                    )
                } catch (e) {
                    return
                }
            }

            const newSenders = [...this.selectedSenders]
            newSenders[index] = normalizedValue
            this.$emit('update:selectedSenders', newSenders)
            this.$emit('senders-change', newSenders)
        },
        addSenderField: function () {
            if (!this.canAddSender) {
                return
            }
            const newSenders = [...this.selectedSenders, null]
            this.$emit('update:selectedSenders', newSenders)
        },
        removeSenderField: async function (index) {
            if (this.selectedSenders.length <= 1) {
                return // Cannot remove the last one
            }

            const removedSenderId = this.selectedSenders[index]
            const confirmed = await this.confirmRemovingSenderIfNeeded(
                removedSenderId
            )
            if (!confirmed) {
                return
            }

            const newSenders = this.selectedSenders.filter(
                (_, i) => i !== index
            )
            // Clean up touched state for removed field and reindex remaining fields
            const newTouchedFields = {}
            this.selectedSenders.forEach((_, i) => {
                if (i < index) {
                    newTouchedFields[i] = this.touchedSenderFields[i]
                } else if (i > index) {
                    newTouchedFields[i - 1] = this.touchedSenderFields[i]
                }
            })
            this.touchedSenderFields = newTouchedFields
            this.$emit('update:selectedSenders', newSenders)
            this.$emit('senders-change', newSenders)
        },
        getSenderError: function (index) {
            if (!this.touchedSenderFields[index]) {
                return []
            }
            const sender = this.selectedSenders[index]
            if (!sender || sender === '') {
                return (
                    this.$t('pages.shipments.senderRequired') ||
                    'Sender is required'
                )
            }
            return []
        },
        markSenderFieldTouched: function (index) {
            this.$set(this.touchedSenderFields, index, true)
        },
        markAllSendersTouched: function () {
            this.selectedSenders.forEach((_, index) => {
                this.$set(this.touchedSenderFields, index, true)
            })
        },
        onRecipientNameChange: function (value) {
            const trimmedValue = (value || '').trim()
            this.$emit('update:recipientFirstName', trimmedValue)
            this.$emit('update:recipientLastName', '')
        },
        onRecipientSelect: function (recipientId) {
            this.$emit('update:selectedRecipientId', recipientId || null)
            if (!recipientId) {
                this.$emit('update:recipientFirstName', '')
                this.$emit('update:recipientLastName', '')
                return
            }
            const selected = this.recipientsItems.find(
                (item) => item.value === recipientId
            )
            const nickName = (selected && selected.text) || ''
            this.$emit('update:recipientFirstName', nickName)
            this.$emit('update:recipientLastName', '')
        },
        onRecipientModeChange: function (mode) {
            this.recipientMode = mode
            this.$emit('update:selectedRecipientId', null)
            this.$emit('update:recipientFirstName', '')
            this.$emit('update:recipientLastName', '')
        },
        getCurrencySymbol: function (currency) {
            console.log(currency)

            return getCurrencySymbol(currency)
        },
    },
}
</script>

<style lang="scss" scoped>
.shipment-form {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: white;
    border-radius: 8px;
    padding: 16px;

    &__header {
        padding-top: 8px;
        margin-bottom: 8px;
    }

    &__senders-section {
        padding-top: 8px;
        margin-bottom: 8px;
    }

    &__section-divider {
        margin: 0 0 16px 0;
        border-color: #e8e8e8;
    }

    &__add-sender-btn {
        margin-bottom: 12px;
    }

    &__sender-field-wrapper {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin-bottom: 12px;
    }

    &__sender-field {
        flex: 1;
    }

    &__remove-sender-btn {
        margin-top: 4px;
        flex-shrink: 0;
    }

    &__recipient {
        margin-bottom: 8px;
    }

    &__recipient-tabs {
        margin-bottom: 12px;
    }

    &__recipient-tabs-items {
        background: transparent;
        padding-top: 4px;
    }

    &__recipient-field {
        width: 100%;
    }

    &__content {
        flex: 1;
        overflow-y: auto;
        padding-right: 8px;
        min-height: 0;

        @include for-hebrew {
            padding-right: 0;
            padding-left: 8px;
        }
    }

    &__section-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 2px solid #f0f0f0;
    }

    &__section-title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin: 0;
    }

    &__items-count {
        font-size: 13px;
        font-weight: 500;
        color: #967adc;
        background-color: #f5f3ff;
        padding: 4px 10px;
        border-radius: 12px;
    }

    &__items-list {
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    &__sender-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0;
        margin-bottom: 4px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__sender-group {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 12px 0 8px;
    }

    &__sender-name {
        font-size: 15px;
        font-weight: 600;
        margin: 0;
        padding: 8px 12px;
        color: #967adc;
        background-color: #f5f3ff;
        border-radius: 6px;
        border-left: 3px solid #967adc;
        letter-spacing: 0.3px;
    }

    &__sender-items {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 0 4px;
    }

    &__sender-divider {
        margin: 20px 0 16px;
        border-color: #e8e8e8;
    }

    &__item-card {
        padding: 16px;
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        background-color: #ffffff;
        transition: all 0.2s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        &:hover {
            border-color: #d0d0d0;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
            transform: translateY(-1px);
        }
    }

    &__item-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;

        :deep(.v-btn) {
            transition: all 0.2s ease;
            opacity: 0.7;

            &:hover {
                opacity: 1;
                transform: scale(1.1);
            }
        }
    }

    &__item-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__item-info-row {
        display: flex;
        gap: 12px;
        font-size: 13px;
        align-items: baseline;
        line-height: 1.5;

        strong {
            min-width: 70px;
            font-weight: 600;
            color: #555;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        span {
            color: #333;
            font-weight: 400;
        }
    }

    &__barcode-value {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }

    &__item-field {
        width: 100%;
        margin-top: 4px;

        :deep(.v-input__control) {
            min-height: 40px;
        }

        :deep(.v-text-field__details) {
            margin-top: 4px;
        }
    }

    &__price-wrapper {
        display: flex;
        align-items: flex-start;
        gap: 8px;
    }

    &__price-input {
        flex: 1;
    }

    &__currency-display {
        width: 24px;
        height: 24px;
        font-size: 18px;
        font-weight: 500;
        color: #333;
        min-width: 24px;
        text-align: center;
        margin-top: 6px;
    }

    &__empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 48px 32px;
        color: #757575;
        text-align: center;

        p {
            margin-top: 16px;
            font-size: 14px;
        }
    }

    &__actions {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        padding: 8px 0 0;
        margin-top: 8px;
        border-top: 1px solid #e0e0e0;
        flex-shrink: 0;
    }
}
</style>
